import React, { forwardRef } from 'react';
import cn from 'classnames';
import { Icon, Spinner } from 'ui';

/*::
type Props = {
  a11yDisabled: boolean,
  a11yDisabledTootip: string,
  children?: any,
  className?: string,
  customColor?: boolean,
  disabled?: boolean,
  fullWidth?: boolean,
  icon?: string,
  loading?: boolean,
  onClick?: () => {},
  primary?: boolean,
  submit?: boolean,
  danger?: boolean,
  tall?: boolean,
  url?: string,
  readOnly?: boolean,
}
*/

const withTooltip = wrappedButton => {
  return (
    <span className="fz-base c-brand-gray-light h-c-gray-darker d-fi ai-c">
      {wrappedButton}
    </span>
  );
};

const Button = forwardRef(
  (
    {
      a11yDisabled,
      a11yDisabledTooltip,
      ariaLabel,
      children,
      className,
      customColor,
      disabled,
      fullWidth,
      icon,
      onlyIconOnMobile,
      loading,
      onClick,
      primary,
      submit,
      danger,
      tall,
      small,
      smaller,
      url,
      readOnly,
      ...buttonProps
    } /*: Props */,
    ref,
  ) => {
    const hasClassName = !!className;
    const hasIcon = !!icon;
    const iconOnly = hasIcon && !children;
    const isLinked = !!url;

    const buttonClassName = cn('Button', {
      'Button--primary': primary,
      'Button--danger': danger,
      'Button--disabled': disabled || a11yDisabled,
      'Button--fullWidth': fullWidth,
      'Button--tall': tall,
      'Button--small': small,
      'Button--smaller': smaller,
      'Button--loading': loading,
      'Button--iconOnly': iconOnly,
      'Button--linked': isLinked,
      'Button--customColor': !disabled && !a11yDisabled && customColor,
      'Button--readOnly': readOnly,
      [className]: hasClassName,
    });

    const content = (
      <div
        className={cn('Button-content', {
          'Button-content--iconOnlyMobile': onlyIconOnMobile,
        })}
      >
        {hasIcon && <Icon name={icon} className="Button-icon" />}
        <span aria-label={`${children}`}>{children}</span>
      </div>
    );

    const standardButton = (
      <button
        ref={ref}
        // https://github.com/yannickcr/eslint-plugin-react/issues/1555
        // This was fixed and should be in the next version after 7.20.6
        // https://github.com/yannickcr/eslint-plugin-react/pull/2748
        // eslint-disable-next-line react/button-has-type
        type={submit ? 'submit' : 'button'}
        onClick={!a11yDisabled ? onClick : undefined}
        className={buttonClassName}
        aria-disabled={a11yDisabled}
        aria-label={
          loading || disabled || readOnly ? `${ariaLabel}: Disabled` : ariaLabel
        }
        data-original-title={
          a11yDisabled && a11yDisabledTooltip ? a11yDisabledTooltip : undefined
        }
        data-toggle="tooltip"
        {...buttonProps}
        disabled={loading || disabled || readOnly}
      >
        {content}
        {loading && <Spinner />}
      </button>
    );

    // eslint-disable-next-line no-nested-ternary
    return isLinked ? (
      <a
        ref={ref}
        className={buttonClassName}
        aria-label="Link Info Tooltip"
        href={!(loading || disabled) && url}
        onClick={onClick}
        {...buttonProps}
      >
        {content}
      </a>
    ) : a11yDisabled ? (
      withTooltip(standardButton)
    ) : (
      standardButton
    );
  },
);

export default Button;
